import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO41001_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO 41001 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Facility Management System (FMS) — International standard for
            efficient, sustainable, and high-performance facility operations.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 41001 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO 41001:2018 is the international standard for Facility
              Management Systems (FMS). It provides a comprehensive framework
              for organizations to establish, implement, operate, monitor,
              review, and improve their facility management practices. ISO 41001
              focuses on enhancing the efficiency, sustainability, and
              performance of facilities, ensuring that facilities are managed
              effectively to meet organizational goals, occupant needs, and
              regulatory requirements.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO 41001 certification demonstrates an organization's
              commitment to high standards in facility management, improving
              operational efficiency, reducing costs, enhancing customer
              satisfaction, and ensuring compliance with relevant standards and
              sustainability goals. It is an essential certification for
              businesses seeking to optimize their facility operations, improve
              energy management, and create a sustainable environment for
              employees, visitors, and stakeholders.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 41001 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 41001 certification establishes a framework for organizations
              to manage and optimize facility operations effectively. It focuses
              on the integration of facility management practices with
              organizational strategies, ensuring that facility services meet
              the needs of both stakeholders and service recipients while
              contributing to broader business objectives. The standard covers a
              wide range of aspects, from facility maintenance and energy
              management to sustainability and cost-effectiveness.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO 41001 aims to:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Optimize facility management performance by aligning strategies with business goals.",
                "Improve the sustainability of operations, addressing environmental, social, and economic factors.",
                "Ensure quality service delivery to facility occupants and stakeholders.",
                "Improve cost management, operational efficiency, and energy performance.",
                "Enhance compliance with legal, regulatory, and environmental standards.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 41001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Integrated Facility Management System (FMS)",
                desc: "ISO 41001 provides a structured framework for managing facility services, ensuring that operations are aligned with organizational objectives, promoting efficiency, sustainability, and occupant satisfaction.",
              },
              {
                icon: Globe,
                title: "Sustainability and Environmental Performance",
                desc: "The standard emphasizes the importance of managing facilities in a way that minimizes environmental impact. This includes energy management, waste reduction, green building practices, and sustainable sourcing.",
              },
              {
                icon: Target,
                title: "Risk and Opportunity Management",
                desc: "ISO 41001 requires organizations to identify and manage risks and opportunities related to facility management. This ensures that the organization is prepared for emergency situations, disruptions, and changing regulations.",
              },
              {
                icon: Users,
                title: "Stakeholder Engagement and Satisfaction",
                desc: "The standard focuses on meeting the needs of facility occupants, employees, and other stakeholders. It emphasizes customer satisfaction by ensuring the facilities support the operational needs of the organization.",
              },
              {
                icon: TrendingUp,
                title: "Continuous Improvement and Monitoring",
                desc: "ISO 41001 supports continuous improvement by encouraging regular monitoring, performance reviews, and audits to ensure that facility management practices remain effective and adapt to changes in business goals or external conditions.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO 41001 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Improved Facility Efficiency — ISO 41001 certification helps organizations streamline their facility management operations, ensuring that resources are used efficiently and costs are minimized. By optimizing maintenance, energy consumption, and resource allocation, organizations can achieve greater operational efficiency.",
                "Sustainability and Compliance — Achieving ISO 41001 ensures that facilities are managed in a way that reduces environmental impact, aligns with sustainability goals, and complies with regulatory requirements. This is particularly important for organizations striving to meet green building certifications or reduce their carbon footprint.",
                "Enhanced Customer and Occupant Satisfaction — By focusing on the needs of facility occupants and stakeholders, ISO 41001 helps businesses improve service delivery, comfort, and safety in the workplace. This leads to better employee productivity, higher tenant satisfaction, and a more positive experience for visitors.",
                "Risk Mitigation — ISO 41001 helps businesses identify and mitigate risks associated with facility operations, including operational disruptions, safety hazards, and non-compliance with regulations. By proactively managing risks, organizations can avoid costly incidents and downtime.",
                "Cost Efficiency — Through effective management of facilities, energy use, and maintenance practices, organizations can significantly reduce operational costs while maintaining the high quality of facility services.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO 41001 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO 41001 outlines a structured approach to managing facility
              operations and ensuring that they meet business needs, comply with
              regulations, and contribute to sustainability. Key components
              include:
            </p>
            <div className="space-y-4">
              {[
                "1. Leadership and Commitment — Senior management must demonstrate leadership and commitment by integrating facility management with business strategy and allocating necessary resources to achieve goals.",
                "2. Facility Management Strategy and Objectives — The organization must develop a facility management strategy that aligns with organizational objectives, stakeholder needs, and sustainability goals.",
                "3. Risk and Opportunity Management — Implement a risk-based approach to identify and assess facility-related risks and opportunities, ensuring that appropriate actions are taken to mitigate risks and seize opportunities.",
                "4. Performance Monitoring and Measurement — Establish key performance indicators (KPIs) to assess the performance of the facility management system. This includes monitoring energy use, service quality, maintenance schedules, and overall facility performance.",
                "5. Stakeholder Engagement — Engage with facility occupants, employees, and other stakeholders to ensure that their needs are met and that their feedback is considered in decision-making.",
                "6. Sustainability and Environmental Considerations — Implement practices that reduce environmental impact, such as energy efficiency, waste management, and water conservation, in line with sustainability objectives.",
                "7. Continuous Improvement — Establish a process for continuous improvement, ensuring that facility management practices are regularly reviewed, refined, and adapted to meet evolving business needs and external conditions.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO 41001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved Operational Efficiency",
                "Compliance with Legal and Regulatory Requirements",
                "Enhanced Stakeholder Satisfaction",
                "Sustainability and Environmental Impact",
                "Risk Mitigation and Business Continuity",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO 41001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The process of obtaining ISO 41001 certification typically
              involves the following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Establish a Facility Management Framework — Develop a facility management strategy and define the scope of your facility management system (FMS).",
                "2. Conduct a Risk Assessment — Identify and assess facility-related risks, including operational disruptions, safety hazards, and environmental impact. Develop strategies to address these risks.",
                "3. Develop Facility Management Plans — Implement plans for maintenance, energy management, service delivery, and sustainability, ensuring alignment with your organizational goals and sustainability objectives.",
                "4. Monitor and Measure Performance — Regularly monitor the performance of your facility management system, using key performance indicators (KPIs) to track energy use, service quality, and operational efficiency.",
                "5. Internal Audits and Documentation — Conduct internal audits to assess the effectiveness of your facility management practices and ensure compliance with ISO 41001 standards.",
                "6. Third-Party Certification Audit — Engage a recognized certification body to conduct a third-party audit and verify that your facility management system (FMS) meets the requirements of ISO 41001.",
                "7. Ongoing Monitoring and Continuous Improvement — Continuously monitor the performance of your facility management system and implement improvements based on audit results, feedback, and emerging industry trends.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 41001 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides expert ISO 41001 certification
              consultancy services, helping businesses optimize their facility
              management systems (FMS) to enhance efficiency, ensure
              sustainability, and improve stakeholder satisfaction.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Facility Management Strategy and Plan Development",
                "Risk Assessment and Mitigation",
                "Performance Monitoring and Reporting",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Improvement and Sustainability Integration",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO 41001 certification, ensuring
              optimized facility operations, sustainability, and customer
              satisfaction.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO41001_Details;
